import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, useTheme } from '@mui/material';
import { LocalOffer } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { alpha } from '@mui/material/styles';
import AnimatedNumber from '../shared/AnimatedNumber';
import { useReducedMotionPreferred } from '../../hooks/useReducedMotionPreferred';
import { GRADIENTS } from '../../theme/tokens';

const MotionBox = motion(Box);

// Sparkle dot — animates in/out on tier 3 tiles
function SparkleDot({ delay }) {
  const reduced = useReducedMotionPreferred();
  if (reduced) return null;

  return (
    <motion.span
      style={{
        display: 'inline-block',
        width: 6,
        height: 6,
        borderRadius: '50%',
        // Using currentColor so it inherits white from parent — no hex literal needed
        backgroundColor: 'currentColor',
        position: 'absolute',
      }}
      animate={{ opacity: [0, 1, 0], scale: [0.6, 1, 0.6] }}
      transition={{ duration: 2.4, delay, repeat: Infinity, ease: 'easeInOut' }}
    />
  );
}

SparkleDot.propTypes = {
  delay: PropTypes.number.isRequired,
};

/**
 * DiscountTile
 * Tier-based coupon medal card for the First-Time Discounts section.
 *
 * Tier visual escalation:
 *  0 (10%) — soft tint, brand-tinted border
 *  1 (20%) — warmer soft gradient, primary.dark text
 *  2 (30%) — GRADIENTS.brand bg, white text
 *  3 (40%) — GRADIENTS.brandDeep bg, white text + glow + sparkles + BEST badge
 *
 * @param {number} discount  — numeric discount value (10|20|30|40)
 * @param {0|1|2|3} tier     — visual intensity tier
 * @param {() => void} onClick
 */
function DiscountTile({ discount, tier, onClick }) {
  const theme = useTheme();
  const reduced = useReducedMotionPreferred();
  const [inView, setInView] = useState(false);

  // --- Per-tier style derivation ---
  const isGradientTile = tier >= 2;
  const isDeepTile = tier === 3;

  const tierStyles = {
    0: {
      background: theme.palette.primary.soft,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
      color: theme.palette.primary.main,
      iconOpacity: 0.3,
    },
    1: {
      background: `linear-gradient(135deg, ${theme.palette.primary.soft} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
      color: theme.palette.primary.dark,
      iconOpacity: 0.5,
    },
    2: {
      background: GRADIENTS.brand,
      border: 'none',
      color: '#ffffff', // white text on brand gradient — explicit white is necessary here
      iconOpacity: 0.7,
    },
    3: {
      background: GRADIENTS.brandDeep,
      border: 'none',
      color: '#ffffff', // white text on deep gradient — explicit white is necessary here
      boxShadow: theme.shadows?.[4] ?? undefined,
      iconOpacity: 1,
    },
  };

  const styles = tierStyles[tier] ?? tierStyles[0];

  const hoverAnim = reduced
    ? {}
    : { y: -6, scale: 1.02 };

  const tapAnim = reduced
    ? {}
    : { scale: 0.97 };

  const spring = { type: 'spring', stiffness: 320, damping: 22 };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <MotionBox
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      whileHover={hoverAnim}
      whileTap={tapAnim}
      transition={spring}
      onViewportEnter={() => setInView(true)}
      viewport={{ once: true, margin: '-60px' }}
      sx={{
        position: 'relative',
        borderRadius: '16px',
        background: styles.background,
        border: styles.border,
        boxShadow: isDeepTile && !reduced ? theme.shadowsExt?.glowPrimary : styles.boxShadow,
        p: { xs: 1.5, md: 2 },
        textAlign: 'center',
        cursor: 'pointer',
        outline: 'none',
        overflow: 'visible',
        userSelect: 'none',
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
      aria-label={`${discount}% off discount filter`}
    >
      {/* LocalOffer icon — top right corner decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          opacity: styles.iconOpacity,
          color: isGradientTile ? 'white' : 'primary.main',
          pointerEvents: 'none',
        }}
      >
        <LocalOffer sx={{ fontSize: 14 }} />
      </Box>

      {/* BEST badge — tier 3 only */}
      {isDeepTile && (
        <Box
          sx={{
            position: 'absolute',
            top: -8,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'white', // explicit white badge on deep-orange bg — necessary for contrast
            color: theme.palette.primary.dark,
            fontSize: '0.55rem',
            fontWeight: 800,
            letterSpacing: '0.08em',
            px: 0.75,
            py: 0.25,
            borderRadius: '999px',
            lineHeight: 1.4,
            zIndex: 2,
          }}
        >
          BEST
        </Box>
      )}

      {/* Sparkle dots — tier 3, above badge area */}
      {isDeepTile && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            color: 'white',
            zIndex: 2,
          }}
        >
          <Box sx={{ position: 'absolute', top: 12, left: 10 }}>
            <SparkleDot delay={0} />
          </Box>
          <Box sx={{ position: 'absolute', bottom: 12, left: 14 }}>
            <SparkleDot delay={0.4} />
          </Box>
          <Box sx={{ position: 'absolute', top: 14, right: 14 }}>
            <SparkleDot delay={0.8} />
          </Box>
        </Box>
      )}

      {/* Discount number */}
      <Typography
        component="div"
        variant="h5"
        fontWeight={800}
        sx={{
          color: styles.color,
          fontSize: { xs: '1.4rem', md: '1.8rem' },
          lineHeight: 1,
          mt: 1,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <AnimatedNumber value={inView ? discount : 0} duration={0.8} />
      </Typography>

      {/* "% OFF" label */}
      <Typography
        variant="caption"
        sx={{
          color: styles.color,
          fontWeight: 700,
          letterSpacing: '0.1em',
          fontSize: { xs: '0.55rem', md: '0.65rem' },
          display: 'block',
          mt: 0.25,
          opacity: isGradientTile ? 0.9 : 1,
        }}
      >
        % OFF
      </Typography>
    </MotionBox>
  );
}

DiscountTile.propTypes = {
  discount: PropTypes.number.isRequired,
  tier: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
  onClick: PropTypes.func.isRequired,
};

export default DiscountTile;
